import { FileStat } from '../api/IFileSystem';
import {
  Download, Copy, Scissors, Trash, FileEdit, Eye, Pin,
  ExternalLink, FolderPlus, ClipboardPaste, ArrowDownAZ, ArrowUpAZ,
  Archive, FolderInput, Check, Upload, File, Folder, FileUp, FolderUp, MapPin, Share2
} from 'lucide-react';
import { t } from '../i18n';

export interface MenuItem {
  id: string;
  label: string;
  icon?: any;
  shortcut?: string;
  disabled?: boolean;
  color?: string;
  separator?: boolean;
  action?: () => void;
  children?: MenuItem[];
  checked?: boolean;
}

interface MenuContext {

  selectedPaths: string[];

  files: FileStat[]; 

  currentPath: string;

  clipboard: { paths: string[] } | null;

  sortConfig: { key: string, direction: 'asc' | 'desc' };

  quickAccessItems: FileStat[];

  actions: {

    download: (paths: string[]) => void;

    share: (paths: string[]) => void;

    copyLink: (path: string) => void;

    preview: (path: string) => void;

    edit: (path: string) => void;

    unzip: (path: string, dest?: string) => void;

    rename: (path: string) => void;

    copy: (cut: boolean) => void;

    delete: (paths: string[]) => void;

    copyPath: (path: string) => void;

    newFolder: () => void;

    paste: () => void;

    sort: (key: string, dir: 'asc' | 'desc') => void;

    pin: (path: string) => void;

    unpin: (path: string) => void;

    uploadFile: (targetPath: string) => void;

    uploadFolder: (targetPath: string) => void;

    locateInRemote: (paths: string[]) => void;

    openInRemote: (paths: string[]) => void;

  };

}



const COMPRESSED_EXTS = ['zip', '7z', 'rar', 'tar', 'gz', 'iso'];

const EDITABLE_EXTS = [

  'txt', 'md', 'json', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 

  'py', 'cpp', 'h', 'c', 'go', 'sql', 'yaml', 'yml', 'xml', 

  'sh', 'bat', 'ps1', 'ini', 'log'

];



export const buildContextMenu = (ctx: MenuContext): MenuItem[] => {

  const { selectedPaths, files, clipboard, sortConfig, actions, quickAccessItems } = ctx;

  // 「在远程桌面中定位 / 打开」一组：定位=远程 Explorer 选中该项，打开=默认程序打开。
  // 两者执行后都会关闭文件管理器(由 action 实现处理)。多选时 action 内部会二次确认。
  const remoteItems = (paths: string[]): MenuItem[] => [
    { id: 'locate-remote', label: t('contextLocateInRemote'), icon: MapPin, action: () => actions.locateInRemote(paths) },
    { id: 'open-remote', label: t('contextOpenInRemote'), icon: ExternalLink, action: () => actions.openInRemote(paths) },
  ];



    // 1. Empty Space (No selection)
    if (selectedPaths.length === 0) {
      return [
        { id: 'new-folder', label: t('contextNewFolder'), icon: FolderPlus, action: actions.newFolder },
        {
          id: 'upload',
          label: t('contextUploadHere'),
          icon: Upload,
          children: [
            { id: 'upload-file', label: t('contextUploadFile'), icon: FileUp, action: () => actions.uploadFile(ctx.currentPath) },
            { id: 'upload-folder', label: t('contextUploadFolder'), icon: FolderUp, action: () => actions.uploadFolder(ctx.currentPath) },
          ]
        },
        { id: 'paste', label: t('contextPaste'), icon: ClipboardPaste, disabled: !clipboard, action: actions.paste },
        { id: 'sep-remote', label: '', separator: true },
        ...remoteItems([ctx.currentPath]),
        { id: 'sep-1', label: '', separator: true },
        {
          id: 'sort',
          label: t('contextSortBy'),
          icon: ArrowDownAZ,
          children: [
            { id: 'sort-name', label: t('contextSortName'), checked: sortConfig.key === 'name', action: () => actions.sort('name', sortConfig.direction) },
            { id: 'sort-type', label: t('contextSortType'), checked: sortConfig.key === 'type', action: () => actions.sort('type', sortConfig.direction) },
            { id: 'sort-size', label: t('contextSortSize'), checked: sortConfig.key === 'size', action: () => actions.sort('size', sortConfig.direction) },
            { id: 'sort-date', label: t('contextSortDate'), checked: sortConfig.key === 'updatedAt', action: () => actions.sort('updatedAt', sortConfig.direction) },
            { id: 'sep-sort', label: '', separator: true },
            { id: 'sort-asc', label: t('contextSortAsc'), checked: sortConfig.direction === 'asc', action: () => actions.sort(sortConfig.key, 'asc') },
            { id: 'sort-desc', label: t('contextSortDesc'), checked: sortConfig.direction === 'desc', action: () => actions.sort(sortConfig.key, 'desc') },
          ]
        }
      ];
  }

  // 2. Multi Selection
  if (selectedPaths.length > 1) {
    // 分享只支持文件：选中项里只要有目录就置灰
    const hasDirSelected = selectedPaths.some(p => {
      const f = files.find(x => x.path === p);
      return !f || f.type === 'dir' || f.type === 'drive';
    });
    return [
      { id: 'download-zip', label: t('contextDownloadZip'), icon: Download, action: () => actions.download(selectedPaths) },
      { id: 'share', label: t('contextShare'), icon: Share2, disabled: hasDirSelected, action: () => actions.share(selectedPaths) },
      { id: 'sep-remote', label: '', separator: true },
      ...remoteItems(selectedPaths),
      { id: 'sep-1', label: '', separator: true },
      { id: 'copy', label: t('contextCopy'), icon: Copy, shortcut: 'Ctrl+C', action: () => actions.copy(false) },
      { id: 'cut', label: t('contextCut'), icon: Scissors, shortcut: 'Ctrl+X', action: () => actions.copy(true) },
      { id: 'delete', label: t('contextDelete'), icon: Trash, shortcut: 'Del', color: 'text-red-400', action: () => actions.delete(selectedPaths) }
    ];
  }

  // 3. Single Selection
  const path = selectedPaths[0];
  const file = files.find(f => f.path === path);
  if (!file) return [];

  const isDir = file.type === 'dir' || file.type === 'drive';
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const isCompressed = !isDir && COMPRESSED_EXTS.includes(ext);
  const isEditable = !isDir && EDITABLE_EXTS.includes(ext);
  const isPinned = quickAccessItems.some(i => i.path === path);

  const baseItems: MenuItem[] = [
    { id: 'download', label: t('contextDownload'), icon: Download, action: () => actions.download([path]) },
    // 分享只支持文件：选中目录时置灰
    { id: 'share', label: t('contextShare'), icon: Share2, disabled: isDir, action: () => actions.share([path]) },
  ];

  if (!isDir) {
    baseItems.push({ id: 'preview', label: t('contextPreview'), icon: Eye, action: () => actions.preview(path) });
    
    if (isEditable) {
      baseItems.push({ id: 'edit', label: t('contextEdit'), icon: FileEdit, action: () => actions.edit(path) });
    }
    
    if (isCompressed) {
        baseItems.push({
            id: 'unzip',
            label: t('contextExtract'),
            icon: Archive,
            children: [
                { id: 'unzip-here', label: t('contextExtractHere'), action: () => actions.unzip(path) },
                { id: 'unzip-to', label: t('contextExtractTo'), icon: FolderInput, action: () => actions.unzip(path, 'PICK_TARGET') }
            ]
        });
    }
  } else {
      // Is Directory
      baseItems.push({
        id: 'upload',
        label: t('contextUploadHere'),
        icon: Upload,
        children: [
          { id: 'upload-file', label: t('contextUploadFile'), icon: FileUp, action: () => actions.uploadFile(path) },
          { id: 'upload-folder', label: t('contextUploadFolder'), icon: FolderUp, action: () => actions.uploadFolder(path) },
        ]
      });
      if (isPinned) {
          baseItems.push({ id: 'unpin', label: t('contextUnpin'), icon: Pin, action: () => actions.unpin(path) });
      } else {
          baseItems.push({ id: 'pin', label: t('contextPin'), icon: Pin, action: () => actions.pin(path) });
      }
  }

  return [
    ...baseItems,
    { id: 'sep-remote', label: '', separator: true },
    ...remoteItems([path]),
    { id: 'sep-1', label: '', separator: true },
    { id: 'rename', label: t('contextRename'), icon: FileEdit, shortcut: 'F2', action: () => actions.rename(path) },
    { id: 'copy', label: t('contextCopy'), icon: Copy, shortcut: 'Ctrl+C', action: () => actions.copy(false) },
    { id: 'cut', label: t('contextCut'), icon: Scissors, shortcut: 'Ctrl+X', action: () => actions.copy(true) },
    { id: 'copy-path', label: t('contextCopyPath'), icon: ClipboardPaste, action: () => actions.copyPath(path) },
    { id: 'sep-2', label: '', separator: true },
    { id: 'delete', label: t('contextDelete'), icon: Trash, shortcut: 'Del', color: 'text-red-400', action: () => actions.delete([path]) }
  ];
};
